import { useTranslation } from 'react-i18next';
import {
  Flex,
  Title,
  Text,
  Stack,
  Loader,
  Button,
  Box,
  NavLink,
} from '@mantine/core';
import { Icon, BetaIcon, BetaText } from '../../../lib/components';
import CopyToClipboard from 'react-copy-to-clipboard';
import { GenericErrorBoundary } from '../../shared/components/GenericErrorBoundary';
import { useTracking, useUser } from '../../hooks';
import { Link } from 'react-router-dom';
import { useToggle } from '@mantine/hooks';
import classes from './Admin.module.css';

type UserDetailsProps = {
  title: string;
  details: (string | undefined)[];
};

export const UserDetail = ({ title, details }: UserDetailsProps) => {
  return (
    <Stack gap="8px">
      <Text fw="bold">{title}</Text>
      {details?.filter(Boolean).map((detail) => (
        <Text key={detail}>{detail}</Text>
      ))}
    </Stack>
  );
};

const UserProfile = () => {
  const { data: user, isLoading } = useUser();
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useToggle();
  const { trackEvent } = useTracking();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join('  ');

  if (isLoading) {
    return <Loader />;
  }

  return (
    <GenericErrorBoundary title={`${t('error.profile')}`}>
      <Flex className={classes.leftContainer}>
        <Flex className={classes.userProfile}>
          <Title order={3}>{user?.business?.name}</Title>

          <UserDetail
            title={t('admin.personalDetails')}
            details={[fullName, user?.email]}
          />

          <UserDetail
            title={t('admin.businessDetails')}
            details={[
              user?.business?.addressLine1,
              user?.business?.addressLine2,
              user?.business?.addressPostcode,
              user?.business?.addressCountry,
            ]}
          />

          <Button
            variant="outline"
            component={Link}
            to="edit-profile"
            radius={6}
            mih={36}
          >
            {t('admin.editProfile')}
          </Button>
        </Flex>
        <Box mt="auto">
          <Box mb={24}>
            <BetaText c="dark.6">{t('admin.business.id')}:</BetaText>
            <Flex gap="md">
              <BetaText>{user?.business?.id}</BetaText>
              <CopyToClipboard
                text={user?.business?.id || ''}
                onCopy={() => {
                  setCopiedId();
                  trackEvent('UserProfile', {
                    step: 'copy-business-id',
                    action: 'copy',
                  });
                }}
                data-testid="client-id-value-copy-button"
              >
                <BetaIcon icon={!copiedId ? 'copy' : 'check'} />
              </CopyToClipboard>
            </Flex>
          </Box>
          {user?.entitlements?.canAccessApi && (
            <NavLink
              to="dev-tools"
              fw="bold"
              component={Link}
              label={t('devTools.title')}
              leftSection={
                <>
                  <Icon icon="chevron-left" />
                  <Icon icon="chevron-right" />
                </>
              }
              className={classes.navLinkContainer}
            />
          )}
        </Box>
      </Flex>
    </GenericErrorBoundary>
  );
};

export default UserProfile;
